import {useState} from 'react';
import {OrganizationMembersTable} from './OrganizationMembersTable';
import {useOrganizationAccounts} from '../hooks/useOrganizationAccounts';
import {addMemberToOrganization} from '../services/OrganizationAccountsService';
import {useQueryClient} from '@tanstack/react-query';
import {
    Alert,
    Button,
    Collapse,
    Group,
    Paper,
    Stack,
    TextInput,
} from '@mantine/core';
import {
    AiOutlineCheckCircle,
    AiOutlineCloseCircle,
    AiOutlineMail,
    AiOutlineSend,
    AiOutlineUserAdd,
} from 'react-icons/ai';

interface OrganizationMembersTabProps {
    organizationId: string;
}

type Feedback = {
    type: 'success' | 'info' | 'error';
    text: string;
} | null;

export const OrganizationMembersTab = ({organizationId}: OrganizationMembersTabProps) => {
    const {data: organizationAccounts, isLoading} = useOrganizationAccounts(organizationId);
    const [showAddForm, setShowAddForm] = useState(false);
    const [email, setEmail] = useState('');
    const [adding, setAdding] = useState(false);
    const [feedback, setFeedback] = useState<Feedback>(null);
    const queryClient = useQueryClient();

    const handleAddMember = async () => {
        if (!email) return;

        setAdding(true);
        setFeedback(null);

        try {
            const result = await addMemberToOrganization(organizationId, email);

            if (result.type === 'DIRECT_ADD') {
                setFeedback({
                    type: 'success',
                    text: `Successfully added ${email} to the organization.`,
                });
                queryClient.invalidateQueries({queryKey: ['organizationAccounts', organizationId]});
            } else if (result.type === 'INVITATION_SENT') {
                setFeedback({
                    type: 'info',
                    text: `Invitation sent to ${email}. They will receive an email to join your organization.`,
                });
            }

            setEmail('');
            setShowAddForm(false);
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'An unexpected error occurred';
            setFeedback({
                type: 'error',
                text: message,
            });
        } finally {
            setAdding(false);
        }
    };

    const feedbackColorMap = {
        success: 'green',
        info: 'blue',
        error: 'red',
    } as const;

    const feedbackIconMap = {
        success: <AiOutlineCheckCircle size={18}/>,
        info: <AiOutlineSend size={18}/>,
        error: <AiOutlineCloseCircle size={18}/>,
    } as const;

    return (
        <Stack gap="lg" p="md">
            <Group justify="flex-end">
                <Button
                    leftSection={<AiOutlineUserAdd size={16}/>}
                    variant={showAddForm ? 'light' : 'filled'}
                    size="sm"
                    onClick={() => setShowAddForm(!showAddForm)}
                >
                    {showAddForm ? 'Cancel' : 'Add Member'}
                </Button>
            </Group>

            <Collapse in={showAddForm}>
                <Paper p="md" radius="sm" withBorder>
                    <Group gap="sm" align="flex-end">
                        <TextInput
                            type="email"
                            placeholder="Enter member email"
                            label="Email Address"
                            leftSection={<AiOutlineMail size={16}/>}
                            value={email}
                            onChange={(e) => setEmail(e.currentTarget.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && email && !adding) {
                                    handleAddMember();
                                }
                            }}
                            style={{flex: 1}}
                        />
                        <Button
                            onClick={handleAddMember}
                            loading={adding}
                            disabled={!email}
                            size="sm"
                        >
                            Send Invite
                        </Button>
                    </Group>
                </Paper>
            </Collapse>

            {feedback && (
                <Alert
                    color={feedbackColorMap[feedback.type]}
                    icon={feedbackIconMap[feedback.type]}
                    withCloseButton
                    onClose={() => setFeedback(null)}
                >
                    {feedback.text}
                </Alert>
            )}

            <OrganizationMembersTable
                organizationAccounts={organizationAccounts?.items || []}
                isLoading={isLoading}
                canManage={true}
            />
        </Stack>
    );
};
